/**
 * Structured answer values and safe parsing/normalization of student input.
 *
 * Adapted from AXLE Algebra for 6th-grade math. Adds DecimalAnswer for
 * topics where the expected answer is a decimal (e.g. decimal operations).
 * Grading always happens on structured values, never raw strings.
 */

import { Rational, rat } from "./rational";

export type InequalityOp = "<" | "<=" | ">" | ">=";

export interface SimpleInequality {
  readonly kind: "inequality";
  readonly op: InequalityOp;
  readonly value: Rational;
}

export interface NumberAnswer {
  readonly kind: "number";
  readonly value: Rational;
}

/**
 * A decimal answer — stored as an exact Rational but the student is expected
 * to enter a decimal. Display and acceptance are decimal-aware.
 */
export interface DecimalAnswer {
  readonly kind: "decimal";
  readonly value: Rational;
  readonly places: number;
}

export interface SpecialAnswer {
  readonly kind: "special";
  readonly value: "no-solution" | "infinitely-many";
}

export interface BooleanAnswer {
  readonly kind: "boolean";
  readonly value: boolean;
}

export interface ChoiceAnswer {
  readonly kind: "choice";
  readonly value: string;
}

export interface SetAnswer {
  readonly kind: "set";
  readonly values: readonly Rational[];
}

export type AnswerValue =
  | NumberAnswer
  | DecimalAnswer
  | SimpleInequality
  | SpecialAnswer
  | BooleanAnswer
  | ChoiceAnswer
  | SetAnswer;

/** Build a canonical SetAnswer: deduped, sorted ascending. */
export function makeSetAnswer(values: readonly Rational[]): SetAnswer {
  const uniq: Rational[] = [];
  for (const v of values) {
    if (!uniq.some((u) => u.equals(v))) uniq.push(v);
  }
  uniq.sort((a, b) => a.compare(b));
  return { kind: "set", values: uniq };
}

export interface ParseSuccess {
  readonly ok: true;
  readonly value: AnswerValue;
}
export interface ParseFailure {
  readonly ok: false;
  readonly reason: string;
}
export type ParseResult = ParseSuccess | ParseFailure;

const ok = (value: AnswerValue): ParseResult => ({ ok: true, value });
const fail = (reason: string): ParseResult => ({ ok: false, reason });

/** Normalize symbol variants and whitespace before parsing. */
export function normalizeInput(raw: string): string {
  return raw
    .trim()
    .replace(/\u2264/g, "<=")
    .replace(/\u2265/g, ">=")
    .replace(/\u2212/g, "-")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

const NO_SOLUTION_SYNONYMS = new Set([
  "no solution",
  "no solutions",
  "none",
  "no sol",
  "empty",
]);

const INFINITE_SYNONYMS = new Set([
  "infinitely many",
  "infinitely many solutions",
  "infinite",
  "infinite solutions",
  "all real numbers",
  "all reals",
]);

const YES_SYNONYMS = new Set(["yes", "y", "true"]);
const NO_SYNONYMS = new Set(["no", "n", "false", "not"]);

/** Parse a rational literal: "6", "-4", "3/5", "-3/5", "3.14". */
function parseRationalLiteral(s: string): Rational | null {
  const cleaned = s.replace(/\s+/g, "");

  // Decimal: "3.14", "-0.5", ".75"
  const dm = /^([+-]?\d*\.\d+)$/.exec(cleaned);
  if (dm) {
    const str = dm[1] as string;
    const dotIdx = str.indexOf(".");
    const intPart = str.slice(0, dotIdx).replace(/^([+-]?)$/, "$10");
    const fracPart = str.slice(dotIdx + 1);
    const whole = intPart + fracPart;
    const den = 10 ** fracPart.length;
    return rat(BigInt(whole), BigInt(den));
  }

  // Integer
  if (/^[+-]?\d+$/.test(cleaned)) {
    return rat(BigInt(cleaned));
  }

  // Fraction a/b
  const m = /^([+-]?\d+)\/([+-]?\d+)$/.exec(cleaned);
  if (m) {
    const num = BigInt(m[1] as string);
    const den = BigInt(m[2] as string);
    if (den === 0n) return null;
    return rat(num, den);
  }
  return null;
}

/** Strip a leading "x =" / "x=" prefix if present. */
function stripVariablePrefix(s: string): string {
  const m = /^[a-z]\s*=\s*(.*)$/.exec(s);
  return m ? (m[1] as string) : s;
}

const OP_TOKENS: readonly InequalityOp[] = [">=", "<=", ">", "<"];

/**
 * Parse a general answer string into a structured AnswerValue.
 */
export function parseAnswer(raw: string): ParseResult {
  const s = normalizeInput(raw);
  if (s === "") return fail("empty input");

  if (NO_SOLUTION_SYNONYMS.has(s)) {
    return ok({ kind: "special", value: "no-solution" });
  }
  if (INFINITE_SYNONYMS.has(s)) {
    return ok({ kind: "special", value: "infinitely-many" });
  }
  if (YES_SYNONYMS.has(s)) return ok({ kind: "boolean", value: true });
  if (NO_SYNONYMS.has(s)) return ok({ kind: "boolean", value: false });

  const simple = tryParseSimpleInequality(s);
  if (simple) return simple;

  const setResult = tryParseSet(s);
  if (setResult) return setResult;

  const stripped = stripVariablePrefix(s);
  const r = parseRationalLiteral(stripped);
  if (r) return ok({ kind: "number", value: r });

  return fail(`could not parse "${raw}"`);
}

function tryParseSet(s: string): ParseResult | null {
  const parts = s.split(/\s*,\s*|\s+or\s+/).filter((p) => p.trim() !== "");
  if (parts.length < 2) return null;
  const values: Rational[] = [];
  for (const part of parts) {
    const r = parseRationalLiteral(stripVariablePrefix(part.trim()));
    if (!r) return null;
    values.push(r);
  }
  return ok(makeSetAnswer(values));
}

function tryParseSimpleInequality(s: string): ParseResult | null {
  for (const op of OP_TOKENS) {
    const idx = s.indexOf(op);
    if (idx === -1) continue;
    const lhs = s.slice(0, idx).trim();
    const rhs = s.slice(idx + op.length).trim();

    if (/^[a-z]$/.test(lhs)) {
      const v = parseRationalLiteral(rhs);
      if (v) return ok({ kind: "inequality", op, value: v });
    }
    if (/^[a-z]$/.test(rhs)) {
      const v = parseRationalLiteral(lhs);
      if (v) return ok({ kind: "inequality", op: flipOp(op), value: v });
    }
    return null;
  }
  return null;
}

function flipOp(op: InequalityOp): InequalityOp {
  switch (op) {
    case "<":
      return ">";
    case ">":
      return "<";
    case "<=":
      return ">=";
    case ">=":
      return "<=";
  }
}

/** Structural equality of two answer values. */
export function equalsAnswer(a: AnswerValue, b: AnswerValue): boolean {
  if (a.kind !== b.kind) {
    // A decimal and number with the same rational value should match.
    if (
      (a.kind === "number" && b.kind === "decimal") ||
      (a.kind === "decimal" && b.kind === "number")
    ) {
      const va = a.kind === "number" ? a.value : a.value;
      const vb = b.kind === "number" ? b.value : b.value;
      return va.equals(vb);
    }
    return false;
  }
  switch (a.kind) {
    case "number":
      return a.value.equals((b as NumberAnswer).value);
    case "decimal":
      return a.value.equals((b as DecimalAnswer).value);
    case "special":
      return a.value === (b as SpecialAnswer).value;
    case "boolean":
      return a.value === (b as BooleanAnswer).value;
    case "choice":
      return a.value === (b as ChoiceAnswer).value;
    case "inequality": {
      const o = b as SimpleInequality;
      return a.op === o.op && a.value.equals(o.value);
    }
    case "set": {
      const o = b as SetAnswer;
      if (a.values.length !== o.values.length) return false;
      return a.values.every((v, i) => v.equals(o.values[i] as Rational));
    }
  }
}

/** Grade student input against an expected answer. */
export function gradeAnswer(raw: string, expected: AnswerValue): boolean {
  const parsed = parseAnswer(raw);
  if (!parsed.ok) return false;
  return equalsAnswer(parsed.value, expected);
}

/** Grade a submission (choice id for choice questions, free text otherwise). */
export function gradeSubmission(
  submission: string,
  expected: AnswerValue,
): boolean {
  if (expected.kind === "choice") {
    return submission === expected.value;
  }
  if (expected.kind === "set" && expected.values.length === 1) {
    const parsed = parseAnswer(submission);
    if (parsed.ok && parsed.value.kind === "number") {
      return parsed.value.value.equals(expected.values[0] as Rational);
    }
  }
  // For decimal answers, accept both fraction and decimal input
  if (expected.kind === "decimal") {
    const parsed = parseAnswer(submission);
    if (parsed.ok && (parsed.value.kind === "number" || parsed.value.kind === "decimal")) {
      return parsed.value.value.equals(expected.value);
    }
    return false;
  }
  return gradeAnswer(submission, expected);
}
